import React, { useState, useEffect } from 'react';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { copyBackupCodes, downloadBackupCodes, printBackupCodes } from '../../utils/backupCodes';
import EmployeeDocuments from './EmployeeDocuments';
import { UserProfileService, IPasswordUpdate } from '../../services/user-profile.service';
import {
    IUserProfile, IUserProfileUpdate, Gender, GENDER_LABELS, ALL_GENDERS,
    PreferredDisbursementMethod, PREFERRED_DISBURSEMENT_LABELS, ALL_PREFERRED_DISBURSEMENT_METHODS,
} from '../../interfaces/user-profile.interface';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/usePermission';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    MenuItem,
    Select,
    FormControl,
    InputLabel,
    Typography,
    Box,
    Alert,
    IconButton,
    TextField,
    Checkbox,
    FormControlLabel,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { ImpersonationGrantService } from '../../services/impersonation-grant.service';
import { TwoFactorService, ITwoFactorStatus, ITwoFactorSetup } from '../../services/two-factor.service';
import { displayUsername } from '../../utils/userUtils';
import { IImpersonationGrant } from '../../interfaces/impersonation-grant.interface';
import { validatePassword } from '../../utils/passwordPolicy';
import { buildProfileClearFields } from '../../utils/profileClearFields';
import { humanizeEnum } from '../../utils/format';
import {
    isValidMobileWalletNumber,
    MOBILE_WALLET_INVALID_MESSAGE,
    MOBILE_PROVIDERS,
    isLegacyMobileProvider,
    LEGACY_PROVIDER_HINT,
} from '../../utils/mobileWallet';
import MyAssetsCard from '../fixedAsset/MyAssetsCard'; // F10
import { helpWhy } from '../../help/why';

const DURATION_OPTIONS = [
    { label: '1 hour', value: 1 },
    { label: '4 hours', value: 4 },
    { label: '1 day', value: 24 },
    { label: '7 days', value: 168 },
];

// Profile-local enrollment confirm form. Distinct from `auth/TwoFactorEnroll`, which drives the
// LOGIN-time enrollment flow and completes via `useAuth().completeTwoFactorLogin`. This form is for
// an already-authenticated user enabling 2FA from their profile, so it completes via
// `TwoFactorService.enable` instead — never wire this to `completeTwoFactorLogin`.
const EnrollConfirmForm: React.FC<{ challengeId: string; onEnrolled: (codes: string[]) => void }> = ({ challengeId, onEnrolled }) => {
    const [code, setCode] = useState('');
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const codes = await TwoFactorService.enable(challengeId, code);
            onEnrolled(codes);
        } catch (err: any) {
            setError(err?.response?.data || err?.message || 'Invalid or expired code');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
                label="Authentication Code"
                data-help="users.myProfile.enroll.code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                fullWidth
                required
                size="small"
                sx={{ mb: 2 }}
            />
            <Button type="submit" variant="contained" fullWidth disabled={submitting} data-help="users.myProfile.enroll.confirm">
                {submitting ? 'Confirming...' : 'Confirm'}
            </Button>
        </form>
    );
};

// D5/H-2/R4: the select's blank placeholder option is the empty string, which is not a valid
// Gender value — the form's local state widens the field to Gender | '' so it can hold that
// placeholder, and the submit handler strips it back out (the joiningDate precedent below).
// Same widening trick as gender above, for the same reason: the blank <select> placeholder is
// the empty string, not a valid PreferredDisbursementMethod member. Picking it over a previously
// saved preference now goes through buildProfileClearFields and is sent as an explicit clear.
type UserProfileFormData = Omit<IUserProfileUpdate, 'gender' | 'preferredDisbursementMethod'> & {
    gender: Gender | '';
    preferredDisbursementMethod: PreferredDisbursementMethod | '';
};

const UserProfile = () => {
    const [profile, setProfile] = useState<IUserProfile | null>(null);
    const [loading, setLoading] = useState(true);
    const toast = useToast();
    const { user, logout } = useAuth();
    const [isEditing, setIsEditing] = useState(false);
    const [isChangingPassword, setIsChangingPassword] = useState(false);
    const [formData, setFormData] = useState<UserProfileFormData>({
        email: '',
        firstName: '',
        lastName: '',
        phone: '',
        bankName: '',
        bankAccountNumber: '',
        mobileBankingProvider: '',
        mobileBankingAccount: '',
        designation: '',
        employeeCode: '',
        joiningDate: '',
        bankRoutingNumber: '',
        bankBranch: '',
        gender: '',
        preferredDisbursementMethod: '',
    });
    const [passwordData, setPasswordData] = useState<IPasswordUpdate>({
        previousPassword: '',
        newPassword: '',
    });
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPasswords, setShowPasswords] = useState({ previousPassword: false, newPassword: false, confirmPassword: false });
    const navigate = useNavigate();

    // Grant access state
    const [activeGrant, setActiveGrant] = useState<IImpersonationGrant | null>(null);
    const [grantDialogOpen, setGrantDialogOpen] = useState(false);
    const [selectedDuration, setSelectedDuration] = useState<number>(1);
    const [grantLoading, setGrantLoading] = useState(false);

    // Two-factor authentication (self-service) state
    const [twoFactorStatus, setTwoFactorStatus] = useState<ITwoFactorStatus | null>(null);
    const [twoFactorSetup, setTwoFactorSetup] = useState<ITwoFactorSetup | null>(null);
    const [enrolledBackupCodes, setEnrolledBackupCodes] = useState<string[] | null>(null);
    const [savedConfirmed, setSavedConfirmed] = useState(false);
    const [disableCode, setDisableCode] = useState('');
    const [isDisabling2fa, setIsDisabling2fa] = useState(false);

    const isSystemAdmin = user?.roles === 'SystemAdmin';
    // The temporary-access grant is an institution-admin tool; ordinary employees never see it.
    const canGrantAccess = useIsAdmin() && !isSystemAdmin;

    const newPasswordErrors = validatePassword(passwordData.newPassword, profile?.username, profile?.phone);

    useEffect(() => {
        fetchUserProfile();
        if (canGrantAccess) {
            fetchActiveGrant();
        }
        TwoFactorService.getStatus().then(setTwoFactorStatus).catch(() => setTwoFactorStatus(null));
    }, []);

    const fetchUserProfile = async () => {
        setLoading(true);
        try {
            const userProfile = await UserProfileService.getLoggedInUserProfile();
            setProfile(userProfile);
            setFormData({
                email: userProfile.email || '',
                firstName: userProfile.firstName || '',
                lastName: userProfile.lastName || '',
                phone: userProfile.phone || '',
                bankName: userProfile.bankName || '',
                bankAccountNumber: userProfile.bankAccountNumber || '',
                mobileBankingProvider: userProfile.mobileBankingProvider || '',
                mobileBankingAccount: userProfile.mobileBankingAccount || '',
                designation: userProfile.designation || '',
                employeeCode: userProfile.employeeCode || '',
                joiningDate: userProfile.joiningDate || '',
                bankRoutingNumber: userProfile.bankRoutingNumber || '',
                bankBranch: userProfile.bankBranch || '',
                gender: userProfile.gender || '',
                preferredDisbursementMethod: userProfile.preferredDisbursementMethod || '',
            });
        } catch (err) {
            console.error('Failed to fetch profile:', err);
            toast.showApiError(err, 'Failed to load user profile. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    const fetchActiveGrant = async () => {
        try {
            const grant = await ImpersonationGrantService.getMyGrant();
            setActiveGrant(grant);
        } catch {
            setActiveGrant(null);
        }
    };

    const handleGrantAccess = async () => {
        setGrantLoading(true);
        try {
            const grant = await ImpersonationGrantService.createGrant(selectedDuration);
            setActiveGrant(grant);
            setGrantDialogOpen(false);
            toast.showSuccess('Admin access granted successfully');
        } catch (err) {
            toast.showApiError(err, 'Failed to grant access');
        } finally {
            setGrantLoading(false);
        }
    };

    const handleRevokeGrant = async () => {
        setGrantLoading(true);
        try {
            await ImpersonationGrantService.revokeMyGrant();
            setActiveGrant(null);
            toast.showSuccess('Admin access revoked');
        } catch (err) {
            toast.showApiError(err, 'Failed to revoke access');
        } finally {
            setGrantLoading(false);
        }
    };

    const handleStartTwoFactorSetup = async () => {
        try {
            const setup = await TwoFactorService.setup();
            setTwoFactorSetup(setup);
        } catch (err) {
            toast.showApiError(err, 'Failed to start two-factor setup');
        }
    };

    const handleTwoFactorEnrolled = (backupCodes: string[]) => {
        setEnrolledBackupCodes(backupCodes);
    };

    const handleCloseEnrollDialog = async () => {
        setTwoFactorSetup(null);
        setEnrolledBackupCodes(null);
        setSavedConfirmed(false);
        const status = await TwoFactorService.getStatus();
        setTwoFactorStatus(status);
        toast.showSuccess('Two-factor authentication enabled');
    };

    const handleDisableTwoFactor = async () => {
        try {
            await TwoFactorService.disable(disableCode);
            setIsDisabling2fa(false);
            setDisableCode('');
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess('Two-factor authentication disabled');
        } catch (err) {
            toast.showApiError(err, 'Failed to disable two-factor authentication');
        }
    };

    const handleRegenerateBackupCodes = async () => {
        const code = window.prompt('Enter your current authentication code to regenerate backup codes:');
        if (!code) return;
        try {
            await TwoFactorService.regenerateBackupCodes(code);
            const status = await TwoFactorService.getStatus();
            setTwoFactorStatus(status);
            toast.showSuccess('Backup codes regenerated. Check your account for the new codes.');
        } catch (err) {
            toast.showApiError(err, 'Failed to regenerate backup codes');
        }
    };

    // Profile-local enroll dialog (distinct from auth/TwoFactorEnroll, which drives the login-time flow).
    // Two-step: confirm the code via TwoFactorService.enable, then show the returned backup codes
    // before closing — mirrors the two-step UX in TwoFactorEnroll.tsx (Task 2).
    const renderEnrollDialog = () => {
        if (!twoFactorSetup) return null;
        return (
            <Dialog open onClose={() => { setTwoFactorSetup(null); setEnrolledBackupCodes(null); setSavedConfirmed(false); }} data-help-page="users.myProfile.enroll.page">
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    {enrolledBackupCodes ? (
                        <>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Each code can be used once if you lose access to your authenticator app. Store them somewhere safe — they won't be shown again.
                            </Typography>
                            <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, mb: 2, fontFamily: 'monospace' }}>
                                {enrolledBackupCodes.map((c) => <div key={c}>{c}</div>)}
                            </Box>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                <Button size="small" data-help="users.myProfile.enroll.copy" onClick={() => copyBackupCodes(enrolledBackupCodes).then(() => toast.showSuccess('Backup codes copied to clipboard'))}>Copy</Button>
                                <Button size="small" data-help="users.myProfile.enroll.download" onClick={() => downloadBackupCodes(enrolledBackupCodes)}>Download</Button>
                                <Button size="small" data-help="users.myProfile.enroll.print" onClick={() => printBackupCodes(enrolledBackupCodes)}>Print</Button>
                            </Box>
                            <FormControlLabel
                                data-help="users.myProfile.enroll.saved"
                                control={<Checkbox checked={savedConfirmed} onChange={(e) => setSavedConfirmed(e.target.checked)} />}
                                label="I've saved these backup codes"
                                sx={{ mb: 2 }}
                            />
                            <Button
                                variant="contained"
                                fullWidth
                                data-help="users.myProfile.enroll.continue"
                                {...helpWhy(!savedConfirmed ? { kind: 'status', message: 'Tick the box to confirm you saved the backup codes.' } : null)}
                                disabled={!savedConfirmed}
                                onClick={handleCloseEnrollDialog}
                            >
                                Continue
                            </Button>
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                                Scan this code with your authenticator app (Google Authenticator, Microsoft Authenticator or similar), then type the 6-digit code it shows to confirm.
                            </Typography>
                            <Box textAlign="center" sx={{ mb: 2 }}>
                                <img src={twoFactorSetup.qrDataUri} alt="Two-factor QR code" style={{ width: 200, height: 200 }} />
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Can't scan? Enter this code manually: <strong>{twoFactorSetup.secret}</strong>
                            </Typography>
                            <EnrollConfirmForm challengeId={twoFactorSetup.challengeId} onEnrolled={handleTwoFactorEnrolled} />
                        </>
                    )}
                </DialogContent>
                {!enrolledBackupCodes && (
                    <DialogActions>
                        <Button data-help="users.myProfile.enroll.cancel" onClick={() => { setTwoFactorSetup(null); setEnrolledBackupCodes(null); setSavedConfirmed(false); }}>
                            Cancel
                        </Button>
                    </DialogActions>
                )}
            </Dialog>
        );
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!profile) return;
        if (!isValidMobileWalletNumber(formData.mobileBankingAccount || '')) return;

        setLoading(true);
        try {
            // joiningDate is a backend LocalDate; an empty string must be omitted rather than
            // sent as '' (risks a 400 / silently coerces to null with no clear intent signaled).
            // gender (H-2/R4): the blank <select> option is the empty string, which is not a
            // valid Gender member — Jackson binding an empty string onto the enum is left to no
            // one's judgment here; it is stripped from the payload entirely, exactly like
            // joiningDate above. Gender is out of scope for the clear-fields work below.
            const { joiningDate, gender, preferredDisbursementMethod, ...rest } = formData;

            // A previously-set clearable field (bank details, employee code, disbursement
            // preference) that is now blank goes into clearFields instead of being posted as ''
            // — the backend rejects a field that is both set and cleared in the same request.
            const clearFields = buildProfileClearFields(profile, { ...rest, preferredDisbursementMethod });
            const clearSet = new Set(clearFields);
            const restCleaned = Object.fromEntries(
                Object.entries(rest).filter(([key]) => !clearSet.has(key))
            ) as typeof rest;

            let payload: IUserProfileUpdate = joiningDate ? { ...restCleaned, joiningDate } : restCleaned;
            if (gender) {
                payload = { ...payload, gender };
            }
            if (preferredDisbursementMethod && !clearSet.has('preferredDisbursementMethod')) {
                payload = { ...payload, preferredDisbursementMethod };
            }
            if (clearFields.length > 0) {
                payload = { ...payload, clearFields };
            }
            const updatedProfile = await UserProfileService.updateUserProfile(
                profile.id,
                payload
            );
            setProfile(updatedProfile);
            setIsEditing(false);
        } catch (err) {
            console.error('Failed to update profile:', err);
            toast.showApiError(err, 'Failed to update profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'confirmPassword') {
            setConfirmPassword(value);
        } else {
            setPasswordData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handlePasswordSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (passwordData.newPassword !== confirmPassword) {
            toast.showError('New password and confirm password do not match');
            return;
        }

        if (newPasswordErrors.length > 0) {
            toast.showError('Password does not meet the strength requirements');
            return;
        }

        setLoading(true);

        try {
            await UserProfileService.updatePassword(passwordData);
            // The server ends every session on a password change (OWASP A07), this one included.
            toast.showSuccess('Password updated. Please log in with your new password.');
            logout();
        } catch (err: any) {
            const body = err?.response?.data;
            const text = typeof body === 'string' ? body : body?.message;
            if (typeof text === 'string' && /previous password/i.test(text)) {
                toast.showError('Current password is incorrect');
            } else {
                toast.showApiError(err, 'Failed to update password. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleLogoutAllDevices = async () => {
        setLoading(true);
        try {
            await UserProfileService.logoutAllDevices();
            toast.showSuccess('Logged out of all devices. Please log in again.');
            logout();
        } catch (err) {
            toast.showApiError(err, 'Failed to log out of all devices. Please try again.');
            setLoading(false);
        }
    };

    if (loading && !profile) {
        return <div>Loading profile...</div>;
    }

    return (
        <div style={{ maxWidth: '600px', margin: '0 auto', padding: '20px' }} data-help-page="users.myProfile.page">
            <h2>User Profile</h2>

            {!isEditing && !isChangingPassword ? (
                <div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Email:</strong> {profile?.email}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>First Name:</strong> {profile?.firstName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Last Name:</strong> {profile?.lastName}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Phone:</strong> {profile?.phone}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Username:</strong> {displayUsername(profile?.username)}
                    </div>
                    {profile?.type && profile.type !== 'User' && (
                        <div style={{ marginBottom: '15px' }}>
                            <strong>Account type:</strong> {humanizeEnum(profile.type)}
                        </div>
                    )}
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Employment</h3>
                        <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#666' }}>Designation, employee code and joining date are maintained by HR. Ask your administrator to change them.</p>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Designation:</strong> {profile?.designation || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Employee Code:</strong> {profile?.employeeCode || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Joining Date:</strong> {profile?.joiningDate ? formatDate(profile.joiningDate) : '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        {/* Fix-round-1 LOW-2: fall back to the raw value if the server ever returns a
                            gender the frontend's GENDER_LABELS map doesn't know about, rather than
                            silently rendering "undefined". */}
                        <strong>Gender:</strong> {profile?.gender ? (GENDER_LABELS[profile.gender] ?? profile.gender) : '-'}
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>Banking</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Name:</strong> {profile?.bankName || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Account Number:</strong> {profile?.bankAccountNumber || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Routing Number:</strong> {profile?.bankRoutingNumber || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Bank Branch:</strong> {profile?.bankBranch || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Mobile Banking Provider:</strong> {profile?.mobileBankingProvider || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Mobile Banking Account:</strong> {profile?.mobileBankingAccount || '-'}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        {/* MFS disbursement lane: "Not set" (not "-") is the deliberate unset
                            wording here — the field is set-only on the backend, so there is no
                            "clear" path through the edit form below either. */}
                        <strong>Preferred Disbursement:</strong> {profile?.preferredDisbursementMethod
                            ? (PREFERRED_DISBURSEMENT_LABELS[profile.preferredDisbursementMethod] ?? profile.preferredDisbursementMethod)
                            : 'Not set'}
                    </div>
                    <button
                        data-help="users.myProfile.edit"
                        onClick={() => setIsEditing(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#007bff',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        Edit Profile
                    </button>
                    <button
                        data-help="users.myProfile.changePassword"
                        onClick={() => setIsChangingPassword(true)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#ffc107',
                            color: 'black',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        Change Password
                    </button>
                    <button
                        data-help="users.myProfile.logoutAllDevices"
                        onClick={handleLogoutAllDevices}
                        disabled={loading}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        Log out of all devices
                    </button>
                    <button
                        data-help="users.myProfile.back"
                        onClick={() => navigate(-1)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Back
                    </button>

                    {/* Grant Access Section - institution admins only */}
                    {canGrantAccess && (
                        <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }} data-help-section="users.myProfile.section.grant">
                            <Typography variant="h6" gutterBottom>Admin Access Grant</Typography>
                            {activeGrant ? (
                                <>
                                    <Alert severity="info" sx={{ mb: 2 }}>
                                        Active grant — expires {formatDateTime(activeGrant.expiresAt)}
                                    </Alert>
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        data-help="users.myProfile.grant.revoke"
                                        onClick={handleRevokeGrant}
                                        disabled={grantLoading}
                                    >
                                        Revoke Access
                                    </Button>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                        Grant SystemAdmin temporary access to manage your institution's data.
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        color="warning"
                                        data-help="users.myProfile.grant.open"
                                        onClick={() => setGrantDialogOpen(true)}
                                        disabled={grantLoading}
                                    >
                                        Grant Admin Access
                                    </Button>
                                </>
                            )}
                        </Box>
                    )}

                    <Box sx={{ mt: 4, p: 2, border: '1px solid #e0e0e0', borderRadius: 2 }} data-help-section="users.myProfile.section.twoFactor">
                        <Typography variant="h6" gutterBottom>Two-Factor Authentication</Typography>
                        {twoFactorStatus?.enabled ? (
                            <>
                                <Alert severity="success" sx={{ mb: 2 }}>
                                    Enabled{twoFactorStatus.backupCodesDisabled ? ' — backup codes blocked by an administrator' : ''}
                                </Alert>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {twoFactorStatus.backupCodesRemaining} backup codes remaining.
                                </Typography>
                                {isDisabling2fa ? (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <TextField
                                            label="Current code"
                                            data-help="users.myProfile.twoFactor.disableCode"
                                            size="small"
                                            value={disableCode}
                                            onChange={(e) => setDisableCode(e.target.value)}
                                        />
                                        <Button variant="contained" color="error" data-help="users.myProfile.twoFactor.confirmDisable" onClick={handleDisableTwoFactor}>
                                            Confirm Disable
                                        </Button>
                                        <Button variant="outlined" data-help="users.myProfile.twoFactor.cancelDisable" onClick={() => { setIsDisabling2fa(false); setDisableCode(''); }}>
                                            Cancel
                                        </Button>
                                    </Box>
                                ) : (
                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button variant="outlined" color="error" data-help="users.myProfile.twoFactor.disable" onClick={() => setIsDisabling2fa(true)}>
                                            Disable Two-Factor Authentication
                                        </Button>
                                        {!twoFactorStatus.backupCodesDisabled && (
                                            <Button variant="outlined" data-help="users.myProfile.twoFactor.regenerate" onClick={handleRegenerateBackupCodes}>
                                                Regenerate Backup Codes
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </>
                        ) : (
                            <>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    Add an extra layer of security to your account with an authenticator app.
                                </Typography>
                                <Button variant="contained" data-help="users.myProfile.twoFactor.enable" onClick={handleStartTwoFactorSetup}>
                                    Enable Two-Factor Authentication
                                </Button>
                            </>
                        )}
                    </Box>

                    <EmployeeDocuments />
                </div>
            ) : isChangingPassword ? (
                <form onSubmit={handlePasswordSubmit} data-help-section="users.myProfile.section.password">
                    <h3>Change Password</h3>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Current Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.previousPassword ? 'text' : 'password'}
                                name="previousPassword"
                            data-help="users.myProfile.password.current"
                                value={passwordData.previousPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                            />
                            <IconButton
                                type="button"
                                data-help="users.myProfile.password.show"
                                onClick={() => setShowPasswords(s => ({ ...s, previousPassword: !s.previousPassword }))}
                                size="small"
                                aria-label={showPasswords.previousPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.previousPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            New Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.newPassword ? 'text' : 'password'}
                                name="newPassword"
                            data-help="users.myProfile.password.new"
                                value={passwordData.newPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={8}
                            />
                            <IconButton
                                type="button"
                                data-help="users.myProfile.password.show"
                                onClick={() => setShowPasswords(s => ({ ...s, newPassword: !s.newPassword }))}
                                size="small"
                                aria-label={showPasswords.newPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.newPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                        {passwordData.newPassword.length > 0 && newPasswordErrors.length > 0 && (
                            <div style={{ color: '#d32f2f', fontSize: '0.75rem', marginTop: '4px' }}>
                                {newPasswordErrors.join('; ')}
                            </div>
                        )}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Confirm New Password:
                        </label>
                        <div style={{ position: 'relative' }}>
                            <input
                                type={showPasswords.confirmPassword ? 'text' : 'password'}
                                name="confirmPassword"
                            data-help="users.myProfile.password.confirm"
                                value={confirmPassword}
                                onChange={handlePasswordChange}
                                style={{ width: '100%', padding: '8px', paddingRight: '40px', boxSizing: 'border-box' }}
                                required
                                minLength={8}
                            />
                            <IconButton
                                type="button"
                                data-help="users.myProfile.password.show"
                                onClick={() => setShowPasswords(s => ({ ...s, confirmPassword: !s.confirmPassword }))}
                                size="small"
                                aria-label={showPasswords.confirmPassword ? 'hide password' : 'show password'}
                                style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)' }}
                            >
                                {showPasswords.confirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                            </IconButton>
                        </div>
                    </div>
                    <button
                        type="submit"
                        data-help="users.myProfile.password.save"
                        {...helpWhy(!loading && newPasswordErrors.length > 0 ? { kind: 'status', message: 'The new password is empty or too weak. Read the red note under the New Password box.' } : null)}
                        disabled={loading || newPasswordErrors.length > 0}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? 'Updating...' : 'Update Password'}
                    </button>
                    <button
                        type="button"
                        data-help="users.myProfile.password.cancel"
                        onClick={() => {
                            setIsChangingPassword(false);
                            setPasswordData({ previousPassword: '', newPassword: '' });
                            setConfirmPassword('');
                        }}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </form>
            ) : (
                <form onSubmit={handleSubmit} data-help-section="users.myProfile.section.edit">
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Email:
                        </label>
                        <input
                            type="email"
                            name="email"
                            data-help="users.myProfile.field.email"
                            value={formData.email}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            First Name:
                        </label>
                        <input
                            type="text"
                            name="firstName"
                            data-help="users.myProfile.field.firstName"
                            value={formData.firstName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Last Name:
                        </label>
                        <input
                            type="text"
                            name="lastName"
                            data-help="users.myProfile.field.lastName"
                            value={formData.lastName}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                            required
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Phone:
                        </label>
                        <input
                            type="text"
                            name="phone"
                            data-help="users.myProfile.field.phone"
                            value={formData.phone}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 4px 0' }}>Employment</h3>
                        <p style={{ margin: '0 0 10px 0', fontSize: 13, color: '#666' }}>Designation, employee code and joining date are maintained by HR. Ask your administrator to change them.</p>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="designation" style={{ display: 'block', marginBottom: '5px' }}>Designation:</label>
                        <input
                            id="designation"
                            data-help="users.myProfile.field.designation"
                            type="text"
                            name="designation"
                            value={formData.designation || ''}
                            onChange={handleChange}
                            disabled
                            style={{ width: '100%', padding: '8px', backgroundColor: '#f5f5f5' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="employeeCode" style={{ display: 'block', marginBottom: '5px' }}>Employee Code:</label>
                        <input
                            id="employeeCode"
                            data-help="users.myProfile.field.employeeCode"
                            type="text"
                            name="employeeCode"
                            value={formData.employeeCode || ''}
                            onChange={handleChange}
                            disabled
                            style={{ width: '100%', padding: '8px', backgroundColor: '#f5f5f5' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="joiningDate" style={{ display: 'block', marginBottom: '5px' }}>Joining Date:</label>
                        <input
                            id="joiningDate"
                            data-help="users.myProfile.field.joiningDate"
                            type="date"
                            name="joiningDate"
                            value={formData.joiningDate || ''}
                            onChange={handleChange}
                            disabled
                            style={{ width: '100%', padding: '8px', backgroundColor: '#f5f5f5' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="gender" style={{ display: 'block', marginBottom: '5px' }}>Gender:</label>
                        <select
                            id="gender"
                            data-help="users.myProfile.field.gender"
                            name="gender"
                            value={formData.gender || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        >
                            <option value="">-</option>
                            {ALL_GENDERS.map(g => (
                                <option key={g} value={g}>{GENDER_LABELS[g]}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ marginTop: '20px', marginBottom: '10px', paddingTop: '10px', borderTop: '1px solid #e0e0e0' }}>
                        <h3 style={{ margin: '0 0 10px 0' }}>Banking</h3>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Bank Name:
                        </label>
                        <input
                            type="text"
                            name="bankName"
                            data-help="users.myProfile.field.bankName"
                            value={formData.bankName || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Bank Account Number:
                        </label>
                        <input
                            type="text"
                            name="bankAccountNumber"
                            data-help="users.myProfile.field.bankAccountNumber"
                            value={formData.bankAccountNumber || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="bankRoutingNumber" style={{ display: 'block', marginBottom: '5px' }}>Bank Routing Number:</label>
                        <input
                            id="bankRoutingNumber"
                            data-help="users.myProfile.field.bankRoutingNumber"
                            type="text"
                            name="bankRoutingNumber"
                            value={formData.bankRoutingNumber || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="bankBranch" style={{ display: 'block', marginBottom: '5px' }}>Bank Branch:</label>
                        <input
                            id="bankBranch"
                            data-help="users.myProfile.field.bankBranch"
                            type="text"
                            name="bankBranch"
                            value={formData.bankBranch || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Mobile Banking Provider:
                        </label>
                        <select
                            name="mobileBankingProvider"
                            data-help="users.myProfile.field.mobileProvider"
                            value={formData.mobileBankingProvider || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        >
                            <option value="">Not set</option>
                            {isLegacyMobileProvider(formData.mobileBankingProvider) && (
                                <option value={formData.mobileBankingProvider || ''}>{formData.mobileBankingProvider}</option>
                            )}
                            {MOBILE_PROVIDERS.map((m) => (
                                <option key={m} value={m}>{m}</option>
                            ))}
                        </select>
                        {isLegacyMobileProvider(formData.mobileBankingProvider) && (
                            <span style={{ color: '#b26a00', fontSize: '0.8em' }}>{LEGACY_PROVIDER_HINT}</span>
                        )}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label style={{ display: 'block', marginBottom: '5px' }}>
                            Mobile Banking Account:
                        </label>
                        <input
                            type="text"
                            name="mobileBankingAccount"
                            data-help="users.myProfile.field.mobileAccount"
                            value={formData.mobileBankingAccount || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        />
                        {!isValidMobileWalletNumber(formData.mobileBankingAccount || '') && (
                            <span style={{ color: 'red', fontSize: '0.8em' }}>{MOBILE_WALLET_INVALID_MESSAGE}</span>
                        )}
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <label htmlFor="preferredDisbursementMethod" style={{ display: 'block', marginBottom: '5px' }}>Preferred Disbursement:</label>
                        <select
                            id="preferredDisbursementMethod"
                            data-help="users.myProfile.field.disbursement"
                            name="preferredDisbursementMethod"
                            value={formData.preferredDisbursementMethod || ''}
                            onChange={handleChange}
                            style={{ width: '100%', padding: '8px' }}
                        >
                            {/* A real clear option: picking this over a previously saved
                                preference sends preferredDisbursementMethod in clearFields
                                (buildProfileClearFields) rather than posting "". */}
                            <option value="">Not set</option>
                            {ALL_PREFERRED_DISBURSEMENT_METHODS.map(m => (
                                <option key={m} value={m}>{PREFERRED_DISBURSEMENT_LABELS[m]}</option>
                            ))}
                        </select>
                    </div>
                    <div style={{ marginBottom: '15px' }}>
                        <strong>Username:</strong> {displayUsername(profile?.username)}
                    </div>
                    {profile?.type && profile.type !== 'User' && (
                        <div style={{ marginBottom: '15px' }}>
                            <strong>Account type:</strong> {humanizeEnum(profile.type)}
                        </div>
                    )}
                    <button
                        type="submit"
                        data-help="users.myProfile.save"
                        {...helpWhy(!loading && !isValidMobileWalletNumber(formData.mobileBankingAccount || '') ? { kind: 'status', message: 'The mobile banking account number is not valid. Use 11 digits starting 013 to 019, or leave it empty.' } : null)}
                        disabled={loading || !isValidMobileWalletNumber(formData.mobileBankingAccount || '')}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#28a745',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            marginRight: '10px'
                        }}
                    >
                        {loading ? 'Saving...' : 'Save Changes'}
                    </button>
                    <button
                        type="button"
                        data-help="users.myProfile.cancel"
                        onClick={() => setIsEditing(false)}
                        style={{
                            padding: '8px 16px',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                </form>
            )}

            {/* Grant Duration Dialog */}
            <Dialog open={grantDialogOpen} onClose={() => setGrantDialogOpen(false)} data-help-page="users.myProfile.grantDialog.page">
                <DialogTitle>Grant Admin Access</DialogTitle>
                <DialogContent sx={{ minWidth: 320, pt: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Select how long SystemAdmin should have access to your institution data.
                    </Typography>
                    <FormControl fullWidth data-help="users.myProfile.grantDialog.duration">
                        <InputLabel>Duration</InputLabel>
                        <Select
                            value={selectedDuration}
                            label="Duration"
                            onChange={(e) => setSelectedDuration(Number(e.target.value))}
                        >
                            {DURATION_OPTIONS.map(opt => (
                                <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setGrantDialogOpen(false)} data-help="users.myProfile.grantDialog.cancel">Cancel</Button>
                    <Button
                        variant="contained"
                        color="warning"
                        data-help="users.myProfile.grantDialog.grant"
                        onClick={handleGrantAccess}
                        disabled={grantLoading}
                    >
                        Grant Access
                    </Button>
                </DialogActions>
            </Dialog>

            {renderEnrollDialog()}

            <MyAssetsCard />{/* F10: assets the caller holds as custodian */}

            <Box sx={{ mt: 3, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2 }} data-help-section="users.myProfile.feedback">
                <Typography variant="body2" sx={{ flex: '1 1 240px' }}>
                    Messages you sent to the Kervolt team and their replies.
                </Typography>
                <Button variant="outlined" onClick={() => navigate('/profile/feedback')} data-help="users.myProfile.feedback.open">
                    My feedback
                </Button>
            </Box>
        </div>
    );
};

export default UserProfile;
